import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Download, Info, Activity, Zap, CheckSquare, Square, ClipboardList, ShieldAlert, FileText, Lock, Unlock, Trash2, ArrowLeft, Share2, Send, CheckCircle2, RotateCcw, Mail, User, MessageSquare } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Area, ComposedChart, Bar } from 'recharts';
import ShareModal from '../components/ShareModal';
import ShareAccessBadge from '../components/ShareAccessBadge';
import { MessageFournisseurButton } from '../components/turpe/MessageFournisseur';
import ValideClientBadge from '../components/ValideClientBadge';
import RapportAuditHistory from '../components/RapportAuditHistory';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PdfPreviewModal from '../components/PdfPreviewModal';
import EligibilityExplainer from '../components/EligibilityExplainer';
import { downloadPdf } from '../utils/downloadPdf';
import SegmentChangeAlert from '../components/turpe/SegmentChangeAlert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RapportTURPE = ({ readOnly = false, shareData = null, shareToken = null }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth?.() || {};
  const userRole = (auth?.user?.role || '').toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'energy_manager';
  const [rapport, setRapport] = useState(shareData);
  const [loading, setLoading] = useState(!shareData);
  const [activeTab, setActiveTab] = useState('overview');
  const [tarifsEnedis, setTarifsEnedis] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const pdfUrl = shareToken
    ? `${API_URL}/api/public/share/${shareToken}/pdf`
    : (id ? `${API_URL}/api/turpe/rapports/${id}/pdf` : null);
  const pdfFilename = `rapport_turpe_${rapport?.client?.nom || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
  const [conditions, setConditions] = useState({
    coherente: null,
    travauxPasses: null,
    travauxFuturs: null,
    reactive: null
  });
  const [clientInfo, setClientInfo] = useState({ prenom: '', nom: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submittedResponse, setSubmittedResponse] = useState(null);

  useEffect(() => {
    // Chargement des tarifs Enedis dynamiques (paramétrables par l'admin)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tarifs-enedis`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTarifsEnedis(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (shareData) {
      setRapport(shareData);
      setLoading(false);
      return;
    }
    const dataParam = searchParams.get('data');
    const previewId = searchParams.get('preview');
    
    if (previewId) {
      // Charger depuis l'API preview
      loadFromPreview(previewId);
    } else if (dataParam) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(dataParam));
        setRapport(decodedData);
        setLoading(false);
      } catch (e) {
        console.error('Erreur décodage données:', e);
        loadRapport();
      }
    } else if (id) {
      loadRapport();
    } else {
      setLoading(false);
    }
  }, [id, searchParams, shareData]);

  // Hydrate la réponse client si déjà soumise
  useEffect(() => {
    const vr = rapport?.validity_response || submittedResponse;
    if (vr?.responses) {
      setConditions({
        coherente: vr.responses.coherente,
        travauxPasses: vr.responses.travauxPasses,
        travauxFuturs: vr.responses.travauxFuturs,
        reactive: vr.responses.reactive,
      });
    }
  }, [rapport, submittedResponse]);

  const submitValidityResponse = async () => {
    if (!shareToken) return;
    if (Object.values(conditions).some((v) => v === null)) {
      toast.error('Veuillez répondre aux 4 questions');
      return;
    }
    if (!clientInfo.prenom.trim() || !clientInfo.nom.trim() || !clientInfo.email.trim()) {
      toast.error('Prénom, nom et email sont requis');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/share/${shareToken}/validity-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prenom: clientInfo.prenom.trim(),
          nom: clientInfo.nom.trim(),
          email: clientInfo.email.trim(),
          responses: { ...conditions },
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de l\'envoi');
      }
      const data = await r.json();
      setSubmittedResponse(data);
      toast.success('Réponses envoyées. Merci !');
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const resetValidityResponse = async () => {
    if (!id) return;
    if (!window.confirm('Renvoyer le rapport pour correction ? La réponse actuelle du client sera effacée et il pourra à nouveau soumettre via son lien de partage.')) return;
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/turpe/rapports/${id}/validity-response`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      toast.success('Réponse réinitialisée — vous pouvez renvoyer le lien au client');
      loadRapport();
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const loadFromPreview = async (previewId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/preview/${previewId}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      } else {
        console.error('Preview expirée ou non trouvée');
      }
    } catch (error) {
      console.error('Erreur chargement preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRapport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${id}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      }
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const filename = `rapport_turpe_${rapport?.client?.nom || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      if (shareToken) {
        await downloadPdf({ url: `/api/public/share/${shareToken}/pdf`, filename, includeAuth: false });
      } else {
        await downloadPdf({ url: `/api/turpe/rapports/pdf`, method: 'POST', body: rapport, filename });
      }
    } catch { /* toast géré par l'utilitaire */ }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce rapport TURPE ?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/turpe/rapports/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    navigate('/dashboard?tab=turpe');
  };

  const handleLock = async () => {
    if (!rapport) return;
    const token = localStorage.getItem('token');
    const action = rapport.locked ? 'unlock' : 'lock';
    await fetch(`${API_URL}/api/turpe/rapports/${id}/${action}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    loadRapport();
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0,00 €';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const formatNumber = (value, unit = '') => {
    if (value === undefined || value === null || isNaN(value)) return 'N/A';
    return value.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + (unit ? ` ${unit}` : '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
      </div>
    );
  }

  if (!rapport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Rapport non trouvé</h1>
          <p className="text-gray-500">Ce rapport n'existe pas ou les données ne sont pas disponibles</p>
        </div>
      </div>
    );
  }

  // Extraire les données
  const { client, pdl, calculs, formuleActuelle, formuleProposee, tensionActuelle, graphData } = rapport;
  const puissancesFactures = rapport.puissancesFactures || [];
  const composantes = calculs?.composantes || {};
  const consommations = calculs?.consommations || {};
  
  const totalActuel = calculs?.totalActuel || 0;
  const totalPropose = calculs?.totalPropose || 0;
  const economieTotal = totalActuel - totalPropose;

  // Préparer les données de puissances factures indexées par mois (YYYY-MM)
  const puissancesFacturesMap = {};
  puissancesFactures.forEach(pf => {
    if (pf.mois && pf.puissance > 0) {
      puissancesFacturesMap[pf.mois.substring(0, 7)] = pf.puissance;
    }
  });
  const hasPuissancesFactures = Object.keys(puissancesFacturesMap).length > 0;

  // Enrichir graphData avec les puissances factures mensuelles
  const graphDataEnriched = graphData?.map(point => {
    if (!hasPuissancesFactures || !point.date) return point;
    const moisKey = point.date.substring(0, 7); // YYYY-MM
    const pFacture = puissancesFacturesMap[moisKey];
    return pFacture ? { ...point, pFacture } : point;
  }) || [];

  // Déterminer si la Pointe est applicable (uniquement HTA)
  const hasPointe = tensionActuelle === 'HTA';
  
  // Périodes tarifaires selon la tension
  const getPeriodesForTension = () => {
    if (tensionActuelle === 'HTA') {
      return ['Pointe', 'HPH', 'HCH', 'HPB', 'HCB'];
    }
    // BTSUP et BTINF n'ont pas de Pointe
    return ['HPH', 'HCH', 'HPB', 'HCB'];
  };
  const periodes = getPeriodesForTension();

  // Nom des composantes en français
  const composantesNoms = {
    'CG': 'Composante de gestion',
    'CC': 'Composante de comptage',
    'CS Fixe': 'Composante de soutirage (part fixe)',
    'CS Variable': 'Composante de soutirage (part variable)',
    'CTA': 'Contribution Tarifaire d\'Acheminement',
    'CER': 'Composante de l\'énergie réactive',
    'CMDPS': 'Composante mensuelle de dépassements'
  };

  // Périodes tarifaires (descriptions)
  const periodesInfo = {
    'Pointe': 'Quelques heures par an, périodes de forte demande hivernale',
    'HPH': 'Heures Pleines Hiver (journées d\'hiver)',
    'HCH': 'Heures Creuses Hiver (nuits d\'hiver)',
    'HPB': 'Heures Pleines Été (journées d\'été)',
    'HCB': 'Heures Creuses Été (nuits d\'été)'
  };

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    { id: 'costs', label: 'Analyse des coûts' },
    { id: 'technical', label: 'Détails techniques' },
    { id: 'validity', label: 'Conditions de validité' },
    { id: 'informations', label: 'Informations' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="rapport-turpe">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button onClick={() => navigate('/dashboard?tab=turpe')} className="p-2 hover:bg-[#F5F6F9] rounded-lg" data-testid="rapport-back-btn">
                <ArrowLeft className="w-5 h-5 text-[#687175]" />
              </button>
            )}
            <img src="/logo.jpg" alt="Place des Énergies" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-gray-400 text-sm">Rapport d'Optimisation TURPE{readOnly ? ' — Lecture seule' : ''}</span>
          </div>
          <div className="flex gap-2">
            <MessageFournisseurButton
              pdls={[{ pdl, tensionActuelle, formuleActuelle, formuleProposee, calculs }]}
              client={client?.nom || rapport.nom_entreprise}
              siren={rapport.siren || client?.siren}
            />
            {readOnly ? (
              <>
                <button onClick={() => window.print()} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Imprimer" data-testid="print-btn">
                  <FileText className="w-5 h-5 text-[#687175]" />
                </button>
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0E7490]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
              </>
            ) : id && (
              <>
                <ShareAccessBadge rapportType="turpe" rapportId={id} />
                <button onClick={() => setShareOpen(true)} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Partager (lecture seule)" data-testid="share-btn">
                  <Share2 className="w-5 h-5 text-[#687175]" />
                </button>
                <ValideClientBadge
                  valide={rapport.valide_client}
                  canToggle={isAdmin}
                  endpoint={`/turpe/rapports/${rapport.id}/toggle-valide-client`}
                  onUpdated={(v) => setRapport({ ...rapport, valide_client: v })}
                />
                {isAdmin && (
                  <button onClick={handleLock} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title={rapport.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid="lock-btn">
                    {rapport.locked ? <Lock className="w-5 h-5 text-[#0891B2]" /> : <Unlock className="w-5 h-5 text-[#687175]" />}
                  </button>
                )}
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0E7490]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
                {isAdmin && (
                  <button onClick={handleDelete} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-btn"><Trash2 className="w-5 h-5" /></button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {!readOnly && id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          rapportType="turpe"
          rapportId={id}
          rapportLabel={rapport?.client?.nom || 'Rapport TURPE'}
        />
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        url={pdfUrl}
        title={`Rapport TURPE — ${rapport?.client?.nom || rapport?.nom_entreprise || ''}`}
        filename={pdfFilename}
      />

      {/* Contenu principal */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Titre */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <Activity className="w-8 h-8 text-[#0891B2]" />
            {client?.nom?.toUpperCase() || 'CLIENT'} {client?.site ? `- ${client.site.toUpperCase()}` : ''}
          </h1>
          <div className="flex flex-wrap gap-4 mt-2 text-gray-500 text-sm">
            <span>PDL : <span className="font-mono">{pdl?.numero || '-'}</span></span>
            <span className="text-gray-300">|</span>
            <span>Formule actuelle : <span className="font-semibold text-gray-700">{formuleActuelle || '-'}</span></span>
            {formuleProposee && formuleProposee !== formuleActuelle && (
              <>
                <span className="text-gray-300">→</span>
                <span>Formule proposée : <span className="font-semibold text-cyan-600">{formuleProposee}</span></span>
              </>
            )}
          </div>
        </div>

        <SegmentChangeAlert
          formuleActuelle={formuleActuelle}
          formuleProposee={formuleProposee}
          tensionActuelle={tensionActuelle}
        />
        <div className="mb-6" />

        {/* Onglets */}
        <div className="bg-[#EEF2F6] rounded-lg p-1 mb-6 flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Carte d'information TURPE */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <h3 className="font-semibold text-gray-900">En quoi consiste les frais d'acheminement ?</h3>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed space-y-3 ml-8">
                <p>
                  Le <strong>TURPE</strong> (Tarif d'Utilisation des Réseaux Publics d'Électricité) est une composante 
                  essentielle de votre facture d'électricité. Il rémunère les gestionnaires de réseaux (RTE et Enedis) 
                  pour l'entretien et le développement des infrastructures électriques.
                </p>
                <p>Ce tarif est déterminé par la Commission de Régulation de l'Énergie (CRE) et comprend :</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Une part fixe</strong> liée à votre puissance souscrite en kVA</li>
                  <li><strong>Une part variable</strong> calculée sur votre consommation en kWh</li>
                </ul>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                  <span className="text-amber-500">💡</span>
                  <p className="text-amber-800">
                    <strong>Bon à savoir :</strong> La part fixe du TURPE peut représenter jusqu'à 40% de vos coûts 
                    d'acheminement. En optimisant votre puissance souscrite, vous réduisez significativement cette part fixe.
                  </p>
                </div>
              </div>
            </div>

            {/* Résumé de l'optimisation */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Résumé de l'optimisation</h3>
              <p className="text-gray-500 text-sm mb-6">Comparaison entre votre situation actuelle et optimisée</p>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm mb-1">Coût Actuel Annuel</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalActuel)}</p>
                </div>
                <div className="bg-cyan-50 rounded-lg p-4">
                  <p className="text-cyan-600 text-sm mb-1">Coût Optimisé Annuel</p>
                  <p className="text-2xl font-bold text-cyan-600">{formatCurrency(totalPropose)}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-emerald-600 text-sm mb-1">Économie Annuelle</p>
                  <p className="text-2xl font-bold text-emerald-500">{formatCurrency(economieTotal)}</p>
                </div>
              </div>
            </div>

            {/* Bandeau méthode de calcul — courbe de charge vs facturation index */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-3" data-testid="turpe-methode-banner">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-900 leading-relaxed">
                <strong>Méthode de calcul : analyse de la courbe de charge.</strong> Les montants présentés
                (en particulier les dépassements de puissance) sont calculés à partir de votre <strong>courbe
                de charge réelle</strong> mesurée par Enedis (pas de 5 à 10 minutes). Votre fournisseur, lui,
                facture sur la base des <strong>index relevés</strong> : un léger écart entre ce rapport et vos
                factures est donc normal et n'affecte pas la pertinence de l'optimisation proposée.
              </div>
            </div>

            {/* Encart pédagogique : comprendre l'optimisation */}
            <EligibilityExplainer
              type="turpe"
              eligible={economieTotal > 0}
              data={{
                economie_totale: economieTotal,
                cout_actuel: totalActuel,
                cout_optimise: totalPropose,
                economies_reactif: 0,
              }}
              accentColor="#0891B2"
            />

            {/* Puissances */}
            <div className="grid grid-cols-2 gap-6">
              {/* Puissances Actuelles */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">Puissances Actuelles</h3>
                <p className="text-gray-500 text-sm mb-4">Vos puissances souscrites actuellement</p>
                <div className="space-y-3">
                  {periodes.map((plage) => (
                    <div key={plage} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-600">{plage}</span>
                      <span className="font-medium text-gray-900">
                        {formatNumber(calculs?.puissancesActuelles?.[plage] || pdl?.puissanceSouscrite || 0)} {tensionActuelle === 'HTA' ? 'kW' : 'kVA'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Puissances Proposées */}
              <div className="bg-white rounded-xl border-2 border-cyan-200 p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-5 h-5 text-cyan-500" />
                  <h3 className="font-semibold text-gray-900">Puissances Proposées</h3>
                </div>
                <p className="text-gray-500 text-sm mb-4">Puissances optimisées recommandées</p>
                <div className="space-y-3">
                  {periodes.map((plage) => (
                    <div key={plage} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-600">{plage}</span>
                      <span className="font-medium text-cyan-600">
                        {formatNumber(calculs?.puissancesProposees?.[plage] || pdl?.puissanceSouscrite || 0)} {tensionActuelle === 'HTA' ? 'kW' : 'kVA'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Analyse de votre situation */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Analyse de votre situation</h3>
                  <p className="text-gray-600 text-sm">
                    {economieTotal > 0 
                      ? `L'optimisation de votre puissance souscrite permettrait une économie annuelle de ${formatCurrency(economieTotal)}. 
                         Cette estimation est basée sur l'analyse de votre courbe de charge et les tarifs TURPE en vigueur.`
                      : 'Votre contrat actuel est déjà optimisé pour votre profil de consommation.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Commentaires de l'analyste — Vue d'ensemble */}
            {(() => {
              const _pdlLabel = (p) => {
                const v = p.pdl ?? p.numero ?? '';
                if (typeof v === 'string' || typeof v === 'number') return String(v);
                if (v && typeof v === 'object') return v.numero || v.pdl || '';
                return '';
              };
              // Mode multi : utilise rapport.pdls[].commentaire
              // Mode mono : utilise rapport.commentaire (top-level) + rapport.pdl (singulier)
              const pdlsForComments = (rapport.pdls && rapport.pdls.length > 0)
                ? rapport.pdls
                : [{ pdl: rapport.pdl, numero: rapport.pdl?.numero, commentaire: rapport.commentaire }];
              const commentairesPDL = pdlsForComments
                .map((p) => ({ pdl: _pdlLabel(p), comment: (p.commentaire || '').trim() }))
                .filter((p) => p.comment);
              if (commentairesPDL.length === 0) return null;
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-8 mt-8" data-testid="section-commentaires">
                  <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-cyan-500" /> Commentaires de l'analyste
                  </h2>
                  <div className="space-y-3">
                    {commentairesPDL.map((p, idx) => (
                      <div key={idx} className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-cyan-700 mb-1 font-mono">PDL {p.pdl}</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{p.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* TAB 2: Analyse des coûts */}
        {activeTab === 'costs' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-1">Comparaison Détaillée des Coûts</h3>
            <p className="text-gray-500 text-sm mb-6">Analyse des 7 composantes du coût d'acheminement (€/an)</p>
            
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 font-medium text-gray-600">Composante</th>
                  <th className="text-right py-3 font-medium text-gray-600">Actuel</th>
                  <th className="text-right py-3 font-medium text-cyan-600">Optimisé</th>
                  <th className="text-right py-3 font-medium text-emerald-600">Économie</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(composantesNoms).map(([key, nom]) => {
                  const actuel = composantes[key]?.actuel || 0;
                  const propose = composantes[key]?.propose || 0;
                  const eco = actuel - propose;
                  const isEconomie = eco > 0; // économie (vert)
                  const isSurcout = eco < 0; // surcoût (rouge)
                  const isIdentique = eco === 0;
                  
                  return (
                    <tr key={key} className="border-b border-gray-100">
                      <td className="py-4 text-gray-700">{nom}</td>
                      <td className="py-4 text-right text-gray-900">{formatCurrency(actuel)}</td>
                      <td className={`py-4 text-right font-medium ${
                        isIdentique ? 'text-gray-500' :
                        isEconomie ? 'text-emerald-500' : 
                        'text-red-500'
                      }`}>
                        {formatCurrency(propose)}
                      </td>
                      <td className={`py-4 text-right font-medium ${
                        isIdentique ? 'text-gray-400' :
                        isEconomie ? 'text-emerald-500' : 
                        'text-red-500'
                      }`}>
                        {eco !== 0 ? (isEconomie ? '+' : '') + formatCurrency(eco) : '-'}
                      </td>
                    </tr>
                  );
                })}
                {/* Total */}
                <tr className="bg-gray-50">
                  <td className="py-4 font-bold text-gray-900">TOTAL</td>
                  <td className="py-4 text-right font-bold text-gray-900">{formatCurrency(totalActuel)}</td>
                  <td className={`py-4 text-right font-bold ${
                    economieTotal > 0 ? 'text-emerald-500' : economieTotal < 0 ? 'text-red-500' : 'text-gray-500'
                  }`}>
                    {formatCurrency(totalPropose)}
                  </td>
                  <td className={`py-4 text-right font-bold ${
                    economieTotal > 0 ? 'text-emerald-500' : economieTotal < 0 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {economieTotal !== 0 ? (economieTotal > 0 ? '+' : '') + formatCurrency(economieTotal) : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* Courbe de charge */}
            {graphData && graphData.length > 0 && (
              <div className="mt-8">
                <h3 className="font-semibold text-gray-900 mb-1">Courbe de charge</h3>
                <p className="text-gray-500 text-sm mb-2">
                  Évolution de la puissance sur la période analysée 
                  <span className="text-cyan-600 ml-2">({graphData.length} points)</span>
                </p>
                <p className="text-xs text-gray-400 mb-4">
                  Évolution de la puissance sur la période analysée
                </p>
                <div className="h-80 bg-gray-50 rounded-lg p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hasPuissancesFactures ? graphDataEnriched : graphData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 9, fill: '#6B7280' }}
                        tickFormatter={(val) => {
                          if (!val) return '';
                          const d = new Date(val);
                          return `${d.getDate()}/${d.getMonth()+1} ${d.getHours()}h`;
                        }}
                        interval="preserveStartEnd"
                        minTickGap={50}
                      />
                      <YAxis 
                        tick={{ fontSize: 10, fill: '#6B7280' }}
                        domain={[0, (dataMax) => {
                          const pSouscrite = pdl?.puissanceSouscrite || 0;
                          const pPreconisee = calculs?.puissancePreconisee || 0;
                          const maxPFacture = hasPuissancesFactures ? Math.max(...Object.values(puissancesFacturesMap)) : 0;
                          const maxRef = Math.max(pSouscrite, pPreconisee, maxPFacture);
                          const maxVal = Math.max(dataMax || 0, maxRef);
                          return Math.ceil(maxVal * 1.1);
                        }]}
                        label={{ value: tensionActuelle === 'HTA' ? 'kW' : 'kVA', angle: -90, position: 'insideLeft', fontSize: 12 }}
                      />
                      <Tooltip 
                        labelFormatter={(val) => {
                          if (!val) return '';
                          return new Date(val).toLocaleString('fr-FR');
                        }}
                        formatter={(value, name) => {
                          if (value === undefined || value === null) return [null, null];
                          return [
                            `${Number(value).toLocaleString('fr-FR', {maximumFractionDigits: 1})} ${tensionActuelle === 'HTA' ? 'kW' : 'kVA'}`,
                            name
                          ];
                        }}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                      />
                      <Legend verticalAlign="top" height={36} />
                      
                      {/* Puissances max factures (barres discrètes en arrière-plan) */}
                      {hasPuissancesFactures && (
                        <Bar
                          dataKey="pFacture"
                          name="P. Max Facture"
                          fill="#F59E0B"
                          fillOpacity={0.15}
                          stroke="#F59E0B"
                          strokeOpacity={0.3}
                          strokeWidth={0}
                          barSize={999999}
                          isAnimationActive={false}
                        />
                      )}
                      
                      {/* Zone sous la courbe kW pour mieux visualiser */}
                      <Area 
                        type="monotone" 
                        dataKey="kW" 
                        fill="#3B82F6" 
                        fillOpacity={0.1}
                        stroke="none"
                      />
                      
                      {/* Puissance kW */}
                      <Line 
                        type="monotone" 
                        dataKey="kW" 
                        name="Puissance (kW)" 
                        stroke="#3B82F6" 
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#3B82F6' }}
                      />
                      {/* Puissance kVA */}
                      <Line 
                        type="monotone" 
                        dataKey="kVA" 
                        name="Puissance (kVA)" 
                        stroke="#8B5CF6" 
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#8B5CF6' }}
                      />
                      
                      {/* Puissance souscrite actuelle (pointillés rouges) */}
                      {pdl?.puissanceSouscrite > 0 && (
                        <ReferenceLine 
                          y={pdl.puissanceSouscrite} 
                          stroke="#EF4444" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: `P. Souscrite: ${pdl.puissanceSouscrite}`, position: 'insideTopRight', fill: '#EF4444', fontSize: 10 }}
                        />
                      )}
                      
                      {/* Puissance préconisée (pointillés cyan) */}
                      {calculs?.puissancePreconisee > 0 && calculs.puissancePreconisee !== pdl?.puissanceSouscrite && (
                        <ReferenceLine 
                          y={calculs.puissancePreconisee} 
                          stroke="#0EA5E9" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: `P. Préconisée: ${calculs.puissancePreconisee}`, position: 'insideBottomRight', fill: '#0EA5E9', fontSize: 10 }}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5 bg-[#3B82F6]"></div>
                    <span>Puissance kW</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5 bg-[#8B5CF6]"></div>
                    <span>Puissance kVA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5" style={{borderTop: '2px dashed #EF4444'}}></div>
                    <span>P. Souscrite actuelle</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-0.5" style={{borderTop: '2px dashed #0EA5E9'}}></div>
                    <span>P. Préconisée</span>
                  </div>
                  {hasPuissancesFactures && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-3 bg-[#F59E0B]/20 border border-[#F59E0B]/40 rounded-sm"></div>
                      <span>P. Max Facture</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tableau comparatif puissances factures vs mesurées */}
            {hasPuissancesFactures && (
              <div className="mt-6" data-testid="tableau-puissances-factures">
                <h3 className="font-semibold text-gray-900 mb-1">Vérification des puissances facturées</h3>
                <p className="text-gray-500 text-sm mb-4">Comparaison entre les puissances maximales mesurées et celles affichées sur vos factures</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mois</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Max Mesurée</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Max Facture</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Écart</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {puissancesFactures.filter(pf => pf.puissance > 0).map((pf, idx) => {
                        // Trouver la puissance max mesurée pour ce mois dans graphData
                        const moisKey = pf.mois.substring(0, 7);
                        const pointsDuMois = graphData?.filter(p => p.date?.startsWith(moisKey)) || [];
                        const pMaxMesuree = pointsDuMois.length > 0
                          ? Math.max(...pointsDuMois.map(p => Math.max(p.kW || 0, p.kVA || 0)))
                          : null;
                        const ecart = pMaxMesuree !== null ? pf.puissance - pMaxMesuree : null;
                        const pSouscrite = pdl?.puissanceSouscrite || 0;
                        const depasse = pf.puissance > pSouscrite && pSouscrite > 0;
                        
                        return (
                          <tr key={idx} className={depasse ? 'bg-red-50' : ''}>
                            <td className="px-4 py-2 text-gray-900 font-medium">{pf.mois}</td>
                            <td className="px-4 py-2 text-right font-mono text-blue-600">
                              {pMaxMesuree !== null ? `${pMaxMesuree.toFixed(1)}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-semibold text-amber-600">
                              {pf.puissance.toFixed(1)}
                            </td>
                            <td className={`px-4 py-2 text-right font-mono ${ecart !== null ? (ecart > 0 ? 'text-amber-600' : 'text-green-600') : 'text-gray-400'}`}>
                              {ecart !== null ? `${ecart > 0 ? '+' : ''}${ecart.toFixed(1)}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {depasse ? (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Dépasse P.S.</span>
                              ) : ecart !== null && ecart > 5 ? (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Écart notable</span>
                              ) : (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Cohérent</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Détails techniques */}
        {activeTab === 'technical' && (
          <div className="space-y-6">
            {/* Périodes tarifaires */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <h3 className="font-semibold text-gray-900">Les périodes tarifaires horosaisonnières</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4 ml-8">
                Votre consommation est répartie selon {periodes.length} périodes tarifaires définies par Enedis. 
                Cette répartition permet d'adapter les tarifs en fonction de la demande sur le réseau électrique national.
              </p>
              <div className="grid grid-cols-2 gap-3 ml-8">
                {periodes.map((plage) => (
                  <div key={plage} className="bg-gray-50 rounded-lg p-3">
                    <span className="text-cyan-600 font-semibold">{plage}</span>
                    <span className="text-gray-600 text-sm"> - {periodesInfo[plage]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Consommations */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Consommations Horosaisonnières</h3>
              <p className="text-gray-500 text-sm mb-6">Répartition de votre consommation par période tarifaire</p>
              
              <div className={`grid gap-4 ${periodes.length === 5 ? 'grid-cols-5' : 'grid-cols-4'}`}>
                {periodes.map((plage) => (
                  <div key={plage} className="bg-gray-50 rounded-lg p-4 text-center">
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">{plage}</p>
                    <p className="text-xl font-bold text-gray-900">
                      {consommations[plage] 
                        ? formatNumber(consommations[plage])
                        : 'N/A'
                      }
                    </p>
                    <p className="text-gray-500 text-xs">kWh</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Informations techniques */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Informations du point de livraison</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Numéro PDL</span>
                  <span className="font-mono text-gray-900">{pdl?.numero || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Domaine de tension</span>
                  <span className="font-medium text-gray-900">{tensionActuelle || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Formule tarifaire actuelle</span>
                  <span className="font-medium text-gray-900">{formuleActuelle || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Formule tarifaire proposée</span>
                  <span className="font-medium text-cyan-600">{formuleProposee || formuleActuelle || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Puissance souscrite</span>
                  <span className="font-medium text-gray-900">
                    {pdl?.puissanceSouscrite || 0} {tensionActuelle === 'HTA' ? 'kW' : 'kVA'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Période analysée</span>
                  <span className="font-medium text-gray-900">{calculs?.nbJours || 0} jours</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: Conditions de validité */}
        {activeTab === 'validity' && (() => {
          const vr = rapport?.validity_response || submittedResponse;
          const hasResponse = !!vr;
          // Mode formulaire client : page partagée, lecture seule, et pas encore de réponse
          const showClientForm = readOnly && !!shareToken && !hasResponse;
          const questions = [
            { key: 'coherente', label: "La consommation électrique de la période étudiée est-elle cohérente par rapport à votre activité ?" },
            { key: 'travauxPasses', label: "Lors des 12 derniers mois, avez-vous réalisé des travaux impactant la consommation électrique ?" },
            { key: 'travauxFuturs', label: "Avez-vous prévu des travaux impactant la consommation électrique lors des 12 prochains mois ?" },
            { key: 'reactive', label: "À votre connaissance, avez-vous de la consommation d'énergie réactive (présence de moteurs ou transformateurs) ?" },
          ];
          const interactive = showClientForm; // boutons OUI/NON cliquables uniquement en mode formulaire client
          const fmtDate = (iso) => {
            try {
              const d = new Date(iso);
              return d.toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
            } catch { return iso; }
          };

          return (
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="validity-tab">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900">Conditions de validité de l'optimisation</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    {hasResponse
                      ? 'Réponses transmises par le client.'
                      : (showClientForm
                          ? "Pour valider cette optimisation, merci de répondre aux 4 questions ci-dessous puis de nous transmettre vos coordonnées."
                          : (readOnly
                              ? "En attente de la réponse du client."
                              : "Pour garantir la pertinence de cette optimisation, le client doit répondre aux questions suivantes via son lien de partage."))}
                  </p>
                </div>
              </div>

              {/* Bandeau "réponse reçue" */}
              {hasResponse && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="validity-response-banner">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-emerald-900 text-sm">
                      Réponses validées par {vr.client?.prenom} {vr.client?.nom}
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      <Mail className="w-3 h-3 inline mr-1" />
                      {vr.client?.email} — transmises le {fmtDate(vr.submitted_at)}
                    </p>
                  </div>
                  {!readOnly && isAdmin && (
                    <button
                      onClick={resetValidityResponse}
                      className="text-xs flex items-center gap-1 px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 rounded-md"
                      data-testid="validity-reset-btn"
                      title="Effacer la réponse du client pour qu'il puisse en soumettre une nouvelle"
                    >
                      <RotateCcw className="w-3 h-3" /> Renvoyer pour correction
                    </button>
                  )}
                </div>
              )}

              {/* Questions */}
              <div className="space-y-6 mt-2">
                {questions.map((q, idx) => (
                  <div key={q.key}>
                    <p className="font-medium text-gray-900 mb-3">
                      <span className="text-gray-400 mr-2">{idx + 1}.</span>{q.label}
                    </p>
                    <div className="flex gap-3">
                      {[true, false].map((val) => {
                        const selected = conditions[q.key] === val;
                        const baseCls = `flex items-center gap-2 px-4 py-2 rounded-lg border ${
                          selected ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-gray-200 text-gray-600'
                        } ${interactive ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'} ${
                          (!interactive && !selected) ? 'opacity-60' : ''
                        }`;
                        const Icon = selected ? CheckSquare : Square;
                        return (
                          <button
                            key={String(val)}
                            type="button"
                            disabled={!interactive}
                            onClick={() => interactive && setConditions({ ...conditions, [q.key]: val })}
                            className={baseCls}
                            data-testid={`validity-q${idx + 1}-${val ? 'oui' : 'non'}`}
                          >
                            <Icon className="w-4 h-4" /> {val ? 'OUI' : 'NON'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bandeau d'évaluation dynamique du pattern de réponses */}
              {(() => {
                const expected = { coherente: true, travauxPasses: false, travauxFuturs: false, reactive: false };
                const keys = Object.keys(expected);
                const allAnswered = keys.every(k => conditions[k] === true || conditions[k] === false);
                if (!allAnswered) {
                  return (
                    <div className="mt-6 p-4 rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-sm" data-testid="validity-pattern-no-response">
                      <strong>Schéma de validité attendu :</strong> OUI / NON / NON / NON.
                      Toute autre combinaison signale qu'une analyse complémentaire est nécessaire avant de garantir les économies projetées.
                    </div>
                  );
                }
                const isIdeal = keys.every(k => conditions[k] === expected[k]);
                if (isIdeal) {
                  return (
                    <div className="mt-6 p-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm" data-testid="validity-pattern-ideal">
                      <strong>Conditions de validité satisfaites.</strong> Les réponses respectent le schéma OUI / NON / NON / NON.
                      L'optimisation présentée est applicable en l'état.
                    </div>
                  );
                }
                return (
                  <div className="mt-6 p-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="validity-pattern-approfondir">
                    <strong>Optimisation à approfondir.</strong> Les réponses ne correspondent pas au schéma attendu (OUI / NON / NON / NON).
                    Une analyse complémentaire est nécessaire avant de garantir les économies projetées —
                    merci de nous contacter pour étudier votre situation.
                  </div>
                );
              })()}

              {/* Formulaire de soumission (mode partage public) */}
              {showClientForm && (
                <div className="mt-8 pt-6 border-t border-gray-200" data-testid="validity-client-form">
                  <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <User className="w-4 h-4" /> Vos coordonnées
                  </h4>
                  <p className="text-xs text-gray-500 mb-4">
                    Ces informations seront archivées avec votre validation.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Prénom</label>
                      <input
                        type="text"
                        value={clientInfo.prenom}
                        onChange={(e) => setClientInfo({ ...clientInfo, prenom: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="validity-prenom"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Nom</label>
                      <input
                        type="text"
                        value={clientInfo.nom}
                        onChange={(e) => setClientInfo({ ...clientInfo, nom: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="validity-nom"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Email</label>
                      <input
                        type="email"
                        value={clientInfo.email}
                        onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="validity-email"
                      />
                    </div>
                  </div>
                  <button
                    onClick={submitValidityResponse}
                    disabled={submitting}
                    className="mt-5 inline-flex items-center gap-2 bg-[#0891B2] hover:bg-[#0E7490] text-white font-medium px-6 py-2.5 rounded-lg text-sm disabled:opacity-60"
                    data-testid="validity-submit-btn"
                  >
                    <Send className="w-4 h-4" /> {submitting ? 'Envoi en cours...' : 'Envoyer mes réponses'}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* TAB 5: Informations (Suite à donner + Tarifs Enedis + Responsabilité) */}
        {activeTab === 'informations' && (
          <div className="space-y-6" data-testid="tab-informations">
            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-suite-a-donner">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-cyan-500" />
                Suite à donner
              </h2>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                <p>En cas d'accord pour la mise en place de l'optimisation TURPE, les étapes suivantes seront engagées :</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Validation conjointe des nouvelles puissances souscrites proposées</li>
                  <li>Préparation et envoi de la demande de modification de puissance auprès du fournisseur d'énergie*</li>
                  <li>Suivi de la mise en œuvre des modifications contractuelles et vérification sur les factures suivantes</li>
                </ul>
                <p className="text-xs text-gray-500 italic mt-2">*Afin de vous accompagner au mieux dans cette étape, un mandat de représentation vous sera transmis pour signature afin d'agir auprès du fournisseur et du gestionnaire de réseau.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-tarifs-enedis">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Tarifs Enedis pour modification de Puissance Souscrite et/ou de FTA
              </h2>
              <p className="text-xs text-gray-500 italic mb-4">
                {tarifsEnedis?.intro_text || "Ces prestations sont à la discrétion du gestionnaire de réseau Enedis. Elles ne sont pas systématiquement facturées. Tarifs HTA et BT > 36 kVA (en vigueur)."}
              </p>
              <div className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">1. Modification de Formule Tarifaire d'Acheminement (FTA)</h3>
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="text-left px-4 py-2">Type d'intervention</th>
                          <th className="text-right px-4 py-2">Prix HT</th>
                          <th className="text-right px-4 py-2">Prix TTC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-testid="tbody-fta-tarifs">
                        {(tarifsEnedis?.fta || [
                          { libelle: "Intervention simple (réglage / mise à jour)", prix_ht: 185.18, prix_ttc: 222.22 },
                          { libelle: "Pose ou remplacement de compteur", prix_ht: 463.89, prix_ttc: 556.67 },
                          { libelle: "Paramétrage à distance", prix_ht: 20.18, prix_ttc: 24.22 },
                          { libelle: "Intervention sur dispositif de télécommunication", prix_ht: 185.18, prix_ttc: 222.22 },
                        ]).map((row, idx) => (
                          <tr key={`fta-${idx}`}>
                            <td className="px-4 py-2">{row.libelle}</td>
                            <td className="text-right px-4 py-2 font-mono">{Number(row.prix_ht).toFixed(2).replace('.', ',')} €</td>
                            <td className="text-right px-4 py-2 font-mono">{Number(row.prix_ttc).toFixed(2).replace('.', ',')} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">2. Modification de la Puissance Souscrite</h3>
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="text-left px-4 py-2">Type d'intervention</th>
                          <th className="text-right px-4 py-2">Prix HT</th>
                          <th className="text-right px-4 py-2">Prix TTC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-testid="tbody-ps-tarifs">
                        {(tarifsEnedis?.ps || [
                          { libelle: "À distance", prix_ht: 75.93, prix_ttc: 91.12 },
                          { libelle: "Intervention simple (réglage / mise à jour)", prix_ht: 75.93, prix_ttc: 91.12 },
                          { libelle: "Changement des TC (Transformateurs de Courant HTA / BT)", prix_ht: 75.93, prix_ttc: 91.12 },
                          { libelle: "Modification de couplage (avec ou sans coupure)", prix_ht: 75.93, prix_ttc: 91.12 },
                          { libelle: "Changement de compteur", prix_ht: 75.93, prix_ttc: 91.12 },
                        ]).map((row, idx) => (
                          <tr key={`ps-${idx}`}>
                            <td className="px-4 py-2">{row.libelle}</td>
                            <td className="text-right px-4 py-2 font-mono">{Number(row.prix_ht).toFixed(2).replace('.', ',')} €</td>
                            <td className="text-right px-4 py-2 font-mono">{Number(row.prix_ttc).toFixed(2).replace('.', ',')} €</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                {tarifsEnedis?.footer_note || "Source : grille tarifaire Enedis. En cas de travaux nécessaires (puissance demandée supérieure à la puissance de raccordement existante), des coûts supplémentaires peuvent être facturés par Enedis et restent à la charge du client."}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-responsabilite">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
                Responsabilité
              </h2>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                <p>Les estimations d'économies présentées dans ce rapport sont établies sur la base des informations et documents transmis par le client (factures, données de consommation, courbes de charge, puissances souscrites). Place des Énergies ne saurait être tenue responsable en cas d'inexactitude ou d'incomplétude des données fournies.</p>
                <p>Les montants indiqués sont des estimations basées sur les consommations et la situation du Point De Livraison sur une période de 12 mois et peuvent varier en fonction des conditions réelles de consommation dans les mois à venir.</p>
                <p>Place Des Energies ne pourra être tenu responsable des frais appliqués par le gestionnaire du réseau pour réaliser la demande de modification (cf. grille tarifaire ci-dessus). Ces coûts sont à la discrétion du gestionnaire de réseau.</p>
                <p>Egalement, Place Des Energies ne peut être tenu responsable en cas de travaux nécessaires de la part du gestionnaire de réseau pour répondre à la demande de modification de puissance souscrite. Ces travaux peuvent nécessiter un coût supplémentaire qui est déterminé par le gestionnaire du réseau lorsque la puissance demandée excède la puissance de raccordement du point.</p>
              </div>
            </div>

            {!readOnly && id && (
              <RapportAuditHistory rapportType="turpe" rapportId={id} />
            )}
          </div>
        )}

        {/* Boutons d'action retirés — actions disponibles dans le header */}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-sm text-gray-400">
          <span>Place des Énergies — Rapport TURPE généré le {new Date().toLocaleDateString('fr-FR')}</span>
          <span>{client?.nom || ''}</span>
        </div>
      </footer>
    </div>
  );
};

export default RapportTURPE;
